const ML_IFRAME_CONTENT_URL =
  /^https:\/\/assets\.mailerlite\.com\/jsonp\/\d+\/forms\/[^/]+\/content(?:[/?#]|$)/i;

const PROXY_FLAG = '__mailerLiteIframeProxyInstalled';

const loadMailerLiteIframeSrcdoc = (iframe: HTMLIFrameElement, url: string): void => {
  iframe.dataset.mailerLiteSourceUrl = url;

  void fetch(url, { credentials: 'omit', mode: 'cors' })
    .then(response => {
      if (!response.ok) {
        throw new Error(`MailerLite iframe proxy fetch failed (${response.status})`);
      }

      return response.text();
    })
    .then(html => {
      const baseTag = '<base href="https://assets.mailerlite.com/">';
      const patchedHtml = html.includes('<head>')
        ? html.replace('<head>', `<head>${baseTag}`)
        : `${baseTag}${html}`;

      iframe.srcdoc = patchedHtml;
    })
    .catch(() => {
      iframe.src = url;
    });
};

const shouldProxyMailerLiteIframeSrc = (value: string): boolean =>
  ML_IFRAME_CONTENT_URL.test(value);

/**
 * Loads MailerLite popup/content iframes via same-origin `srcdoc` so the parent page can read
 * submitted email fields for CAPI tracking. Falls back to the original cross-origin `src` when
 * fetch fails.
 *
 * Must run before MailerLite universal.js creates popup iframes (safe to call multiple times).
 */
export const installMailerLiteIframeProxy = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const win = window as Window & { [PROXY_FLAG]?: boolean };
  if (win[PROXY_FLAG]) {
    return;
  }

  win[PROXY_FLAG] = true;

  const nativeSetAttribute = Element.prototype.setAttribute;
  Element.prototype.setAttribute = function setAttribute(
    this: Element,
    name: string,
    value: string,
  ) {
    if (
      this instanceof HTMLIFrameElement &&
      name === 'src' &&
      typeof value === 'string' &&
      shouldProxyMailerLiteIframeSrc(value)
    ) {
      loadMailerLiteIframeSrcdoc(this, value);
      return;
    }

    return nativeSetAttribute.call(this, name, value);
  };

  const srcDescriptor = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'src');
  if (srcDescriptor?.set && srcDescriptor.get) {
    Object.defineProperty(HTMLIFrameElement.prototype, 'src', {
      configurable: true,
      enumerable: srcDescriptor.enumerable ?? true,
      get: srcDescriptor.get,
      set(value: string) {
        if (typeof value === 'string' && shouldProxyMailerLiteIframeSrc(value)) {
          loadMailerLiteIframeSrcdoc(this, value);
          return;
        }

        srcDescriptor.set!.call(this, value);
      },
    });
  }
};

/** Self-contained inline bootstrap for `next/script` with `beforeInteractive`. */
export const getMailerLiteIframeProxyScript = (): string => `(function(){
  if(typeof window==='undefined'||window.__mailerLiteIframeProxyInstalled)return;
  window.__mailerLiteIframeProxyInstalled=true;
  var ML_IFRAME_CONTENT_URL=/^https:\\/\\/assets\\.mailerlite\\.com\\/jsonp\\/\\d+\\/forms\\/[^/]+\\/content(?:[/?#]|$)/i;
  function loadMailerLiteIframeSrcdoc(iframe,url){
    iframe.dataset.mailerLiteSourceUrl=url;
    fetch(url,{credentials:'omit',mode:'cors'}).then(function(r){
      if(!r.ok)throw new Error('MailerLite iframe proxy fetch failed ('+r.status+')');
      return r.text();
    }).then(function(html){
      var baseTag='<base href="https://assets.mailerlite.com/">';
      iframe.srcdoc=html.includes('<head>')?html.replace('<head>','<head>'+baseTag):baseTag+html;
    }).catch(function(){iframe.src=url;});
  }
  var nativeSetAttribute=Element.prototype.setAttribute;
  Element.prototype.setAttribute=function(name,value){
    if(this instanceof HTMLIFrameElement&&name==='src'&&typeof value==='string'&&ML_IFRAME_CONTENT_URL.test(value)){
      loadMailerLiteIframeSrcdoc(this,value);
      return;
    }
    return nativeSetAttribute.call(this,name,value);
  };
  var srcDescriptor=Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype,'src');
  if(srcDescriptor&&srcDescriptor.set&&srcDescriptor.get){
    Object.defineProperty(HTMLIFrameElement.prototype,'src',{
      configurable:true,
      enumerable:srcDescriptor.enumerable!==false,
      get:srcDescriptor.get,
      set:function(value){
        if(typeof value==='string'&&ML_IFRAME_CONTENT_URL.test(value)){
          loadMailerLiteIframeSrcdoc(this,value);
          return;
        }
        return srcDescriptor.set.call(this,value);
      }
    });
  }
})();`;
